<template>
	<section>
		<el-table
			:data="tableData"
			v-loading="loading"
			:empty-text="loading ? $t('openClawManagement.loading') : $t('openClawManagement.noData')"
			border
			row-key="user_id"
			style="width: 100%"
		>
			<el-table-column prop="guest_name" :label="$t('openClawManagement.guestName')" min-width="150">
				<template slot-scope="scope">
					<span class="guest-name-text">{{ scope.row.guest_name || "--" }}</span>
				</template>
			</el-table-column>

			<el-table-column prop="guest_phone_number" :label="$t('openClawManagement.guestPhoneNumber')" min-width="100">
				<template slot-scope="scope">
					<span class="guest-phone-text">{{ scope.row.guest_phone_number || "--" }}</span>
				</template>
			</el-table-column>

			<el-table-column :label="$t('openClawManagement.associatedRestaurantId')" min-width="100">
				<template slot-scope="scope">
					<div v-if="scope.row.restaurant_ids.length > 0" class="multi-line-cell">
						<span
							v-for="(customerId, index) in scope.row.restaurant_ids"
							:key="`${scope.row.user_id}-customer-id-${index}`"
							class="multi-line-item customer-id-chip"
						>
							{{ customerId }}
						</span>
					</div>
					<span v-else>--</span>
				</template>
			</el-table-column>

			<el-table-column :label="$t('openClawManagement.restaurantName')" min-width="200">
				<template slot-scope="scope">
					<div v-if="scope.row.restaurant_names.length > 0" class="multi-line-cell">
						<span
							v-for="(restaurantName, index) in scope.row.restaurant_names"
							:key="`${scope.row.user_id}-customer-name-${index}`"
							class="multi-line-item restaurant-name-chip"
						>
							{{ restaurantName }}
						</span>
					</div>
					<span v-else>--</span>
				</template>
			</el-table-column>

			<el-table-column :label="$t('openClawManagement.status')" width="150" align="center">
				<template slot-scope="scope">
					<el-tag :type="scope.row.access_status ? 'success' : 'danger'" size="mini">
						{{ getStatusText(scope.row.access_status) }}
					</el-tag>
				</template>
			</el-table-column>

			<el-table-column :label="$t('openClawManagement.operate')" width="200" fixed="right" align="center">
				<template slot-scope="scope">
					<el-button
						size="mini"
						type="primary"
						:plain="scope.row.access_status"
						class="action-button"
						:loading="isRowUpdating(scope.row.user_id)"
						:disabled="isRowUpdating(scope.row.user_id)"
						@click="onToggle(scope.row)"
					>
						{{ getActionText(scope.row.access_status) }}
					</el-button>
				</template>
			</el-table-column>
		</el-table>
	</section>
</template>

<script>
import { getOpenClawUsers, putOpenClawUserAccessStatus } from "@/services/api-v2/api-v2";

const {
	getActionText,
	getNextAccessStatus,
	getRestaurantIds,
	getRestaurantNames,
	getStatusText,
	normalizeOpenClawUsers,
} = require("./open-claw-management.helper");

export default {
	name: "OpenClawManagement",

	data() {
		return {
			loading: false,
			tableData: [],
			updatingRows: {},
		};
	},

	created() {
		this.loadOpenClawUsers();
	},

	methods: {
		buildTableData(users) {
			return normalizeOpenClawUsers(users).map((user) => ({
				...user,
				restaurant_ids: getRestaurantIds(user.customers),
				restaurant_names: getRestaurantNames(user.customers),
			}));
		},

		async loadOpenClawUsers() {
			this.loading = true;

			try {
				const { data } = await getOpenClawUsers();
				this.tableData = this.buildTableData(data);
			} catch (error) {
				this.$message.error(error);
			} finally {
				this.loading = false;
			}
		},

		getStatusText(accessStatus) {
			return getStatusText(accessStatus, (key) => this.$t(key));
		},

		getActionText(accessStatus) {
			return getActionText(accessStatus, (key) => this.$t(key));
		},

		isRowUpdating(userId) {
			return Boolean(this.updatingRows[userId]);
		},

		async onToggle(row) {
			const nextAccessStatus = getNextAccessStatus(row.access_status);

			this.$set(this.updatingRows, row.user_id, true);

			try {
				await putOpenClawUserAccessStatus(row.user_id, nextAccessStatus);
				row.access_status = nextAccessStatus;
				this.$message.success(this.$t("openClawManagement.updateSuccess"));
			} catch (error) {
				this.$message.error(error);
			} finally {
				this.$delete(this.updatingRows, row.user_id);
			}
		},
	},
};
</script>

<style scoped>
::v-deep .el-table th {
	background-color: #fff;
	color: #8c8c8c;
	font-weight: 500;
}

::v-deep .el-table td {
	color: #606266;
}

::v-deep .el-table .cell {
	line-height: 24px;
}

.multi-line-cell {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.multi-line-item {
	display: inline-flex;
	align-items: center;
	width: fit-content;
	line-height: 20px;
	word-break: break-all;
	padding: 0 6px;
	border-radius: 4px;
	font-size: 12px;
}

.guest-name-text {
	color: #606266;
}

.guest-phone-text {
	color: #409eff;
}

.customer-id-chip {
	color: #66b1ff;
	background-color: #f4f9ff;
	border: 1px solid #dbeafe;
}

.restaurant-name-chip {
	color: #7ec050;
	background-color: #f3faeb;
	border: 1px solid #dcefcf;
}

::v-deep .action-button {
	min-width: 56px;
	padding: 7px 14px;
	border-radius: 4px;
}

::v-deep .action-button.el-button--primary {
	background-color: #2f6df6;
	border-color: #2f6df6;
}

::v-deep .action-button.el-button--primary.is-plain {
	color: #2f6df6;
	background-color: #ffffff;
	border-color: #9db7ff;
}

::v-deep .action-button.el-button--primary.is-plain:hover,
::v-deep .action-button.el-button--primary.is-plain:focus {
	background-color: #f4f8ff;
	border-color: #7ea3ff;
	color: #2f6df6;
}
</style>
